import {
  useLazyGetPlayDataQuery,
  useLazyGetPlaybookPreviewQuery,
  useLazyGetPlaybookQuery,
} from '@carboncopyco/data';
import {
  type ExternalUrlPresentationByTokenResponse,
  type Play,
  type PlayData,
  type PlayPresentation,
  type PresentationByTokenResponse,
  type PresentationItemPlayData,
  RecordingTypes,
  type TourPresentationByTokenResponse,
} from '@carboncopyco/types';
import {
  type GlobalTabType,
  TabGroupName,
  TabType,
} from '@carboncopyco/types/desktop';
import type { SpeakerPanelData } from '@carboncopyco/types/desktop';
import {
  SettingMainItemsKeys,
  SettingsSubKeys,
} from '@carboncopyco/types/desktop/appSettings';
import { generateGroupId } from '../../util/tab-util';
import { useTabManager } from '../services/TabManager';
import { useSpeakerPanelContext, useWorkspaces } from '../store/context';
import { useFlagsFallback } from './useFlagsFallback';

const getPlaybookDemoPresentationLink = (
  organizationId: string,
  workspaceId: string,
  assetId: string
) =>
  `organization/${organizationId}/workspace/${workspaceId}/playbook-presentation/demo/${assetId}/present`;

const getPresentationType = (
  presentation: PresentationItemPlayData['playData']
) => {
  if ('demoId' in presentation && 'presentationId' in presentation)
    return 'demo';
  if ('tour' in presentation) return 'tour';
  if ('url' in presentation) return 'externalUrl';
  return undefined;
};

const playPresentationPerType = (
  presentation: PresentationItemPlayData['playData'],
  {
    onPlayDemo,
    onPlayTour,
    onPlayExternalUrl,
  }: {
    onPlayDemo?: (presentation: PresentationByTokenResponse) => void;
    onPlayTour?: (presentation: TourPresentationByTokenResponse) => void;
    onPlayExternalUrl?: (
      presentation: ExternalUrlPresentationByTokenResponse
    ) => void;
  }
) => {
  const presentationType = getPresentationType(presentation);
  if (presentationType === 'demo') {
    onPlayDemo?.(presentation as PresentationByTokenResponse);
  }
  if (presentationType === 'tour') {
    onPlayTour?.(presentation as TourPresentationByTokenResponse);
  }
  if (presentationType === 'externalUrl') {
    onPlayExternalUrl?.(presentation as ExternalUrlPresentationByTokenResponse);
  }
};

const getMergingOptionKey = (
  presentation: PresentationItemPlayData['playData']
) => {
  const presentationType = getPresentationType(presentation);
  if (presentationType === 'demo') {
    return Number((presentation as PresentationByTokenResponse).presentationId);
  }
  if (presentationType === 'tour') {
    return Number(
      (presentation as TourPresentationByTokenResponse).tourPresentationId
    );
  }
  if (presentationType === 'externalUrl') {
    return Number(
      (presentation as ExternalUrlPresentationByTokenResponse).presentationId
    );
  }
  return Number((presentation as PresentationByTokenResponse).presentationId);
};

type PlayHandlerProps = {
  workspaceId: string;
  playSubset: PlaySubset;
  groupId: number;
  name: TabGroupName;
  type: TabType;
  index: number;
  shouldOpenInSeparateTabs: boolean;
  analyticsSessionId: string;
};

type PlaySubset = {
  id: string;
  forClient: string;
  presentations: PlayPresentation[] | PresentationItemPlayData[];
  playbookId?: string;
  isDeleted?: boolean;
  workspaceId: string;
};

type PlaybookPresentationsProps = {
  playSubset: PlaySubset;
  data?: PlayData;
  isPreview?: boolean;
  shouldClosePreviewTab?: boolean;
};

const getScreenshotImageType = (
  presentation: PresentationItemPlayData['playData']
) => {
  const presentationType = getPresentationType(presentation);
  if (presentationType === 'tour') return 'tour' as const;
  if (presentationType === 'externalUrl') return 'url' as const;
  if (
    presentationType === 'demo' &&
    'recordingType' in presentation &&
    presentation.recordingType === RecordingTypes.overlay
  )
    return 'overlay' as const;
  return 'demo' as const;
};

export const usePlaybookPresentations = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const [getPlayData] = useLazyGetPlayDataQuery();
  const { createGlobalTab, globalTabs, closeGlobalTabs } = useTabManager();
  const [getPlaybookPreview] = useLazyGetPlaybookPreviewQuery();
  const [getPlaybook] = useLazyGetPlaybookQuery();
  const { setIsSpeakerPanelWarningVisible } = useSpeakerPanelContext();
  const { activeWorkspace } = useWorkspaces();
  const { multiTabDisplay } = useFlagsFallback();

  const closePlayPreviewTabs = async (assetId: string) => {
    const tabsToClose = globalTabs.filter(
      (tab) =>
        tab.group?.name === TabGroupName.PlaybookPreview &&
        // biome-ignore lint/suspicious/noDoubleEquals: <explanation>
        (tab.payload?.play?.playbookId == assetId || // for playbook preview
          // biome-ignore lint/suspicious/noDoubleEquals: <explanation>
          tab.payload?.play?.asset?.assetId == assetId) // for playbook presentation preview
    );

    if (tabsToClose.length) {
      closeGlobalTabs(tabsToClose);
    }
  };

  const handlePlayDemo = ({
    playSubset,
    groupId,
    name,
    type,
    presentation,
    index,
    shouldOpenInSeparateTabs,
    analyticsSessionId,
    workspaceId,
  }: PlayHandlerProps & {
    presentation: PresentationByTokenResponse;
  }) => {
    const isFirstPresentation = index === 0;

    createGlobalTab({
      type,
      workspaceId,
      title: playSubset.forClient,
      hoverTitle: playSubset.forClient,
      url: 'https://', // TODO: check how to remove this. Currently it's needed to be able to load the url.
      payload: {
        play: playSubset,
        presentation,
        forClient: playSubset.forClient,
        showWithoutDrafts: true,
      },
      routeTo: getPlaybookDemoPresentationLink(
        organizationId,
        activeWorkspace?.current?.id || presentation.workspaceId,
        presentation.demoId
      ),
      group: {
        id: groupId,
        name,
        isVisibleIfGroupIsActive:
          isFirstPresentation || shouldOpenInSeparateTabs,
        isVisibleIfGroupIsInactive: isFirstPresentation,
        showNonRelatedGroupTabsIfActive: false,
        focusOnCreate: isFirstPresentation,
        closeGroupOnClose: true,
        mergingOptions: {
          allowed: true,
          key: Number(presentation.presentationId),
          index,
          isDetached: isFirstPresentation ? false : shouldOpenInSeparateTabs,
          detachedTitle: presentation.name,
        },
      },
      analyticsSessionId,
      presentationId: String(presentation.presentationId),
      presentationType: 'presentation',
      playId: playSubset.id,
      forClient: playSubset.forClient,
    });
  };

  const handlePlayTour = ({
    playSubset,
    groupId,
    name,
    type,
    presentation,
    index,
    shouldOpenInSeparateTabs,
    analyticsSessionId,
    workspaceId,
  }: PlayHandlerProps & {
    presentation: TourPresentationByTokenResponse;
  }) => {
    const isFirstPresentation = index === 0;

    createGlobalTab<GlobalTabType>({
      type,
      workspaceId,
      title: playSubset.forClient,
      url: 'https://',
      hoverTitle: playSubset.forClient,
      payload: {
        tour: presentation.tour,
        play: playSubset,
        presentation,
      },
      group: {
        id: groupId,
        name,
        isVisibleIfGroupIsActive:
          isFirstPresentation || shouldOpenInSeparateTabs,
        isVisibleIfGroupIsInactive: isFirstPresentation,
        showNonRelatedGroupTabsIfActive: false,
        focusOnCreate: isFirstPresentation,
        closeGroupOnClose: true,
        mergingOptions: {
          allowed: true,
          key: Number(presentation.tourPresentationId),
          index,
          isDetached: isFirstPresentation ? false : shouldOpenInSeparateTabs,
          detachedTitle: presentation.name,
        },
      },
      analyticsSessionId,
      forClient: playSubset.forClient,
      presentationId: String(presentation.tourPresentationId),
      presentationType: 'tour_presentation',
      playId: playSubset.id,
      createGlobalTabOnElectron: {
        startOn: 'component-load',
        withInterceptor: false,
        withWebSecurity: false,
      },
    });
  };

  const handlePlayExternalUrl = ({
    playSubset,
    groupId,
    name,
    type,
    presentation,
    index,
    shouldOpenInSeparateTabs,
    analyticsSessionId,
    workspaceId,
  }: PlayHandlerProps & {
    presentation: ExternalUrlPresentationByTokenResponse;
  }) => {
    const isFirstPresentation = index === 0;

    createGlobalTab<GlobalTabType>({
      type,
      workspaceId,
      title: playSubset.forClient,
      hoverTitle: playSubset.forClient,
      url: presentation.url,
      payload: {
        play: playSubset,
        presentation,
      },
      group: {
        id: groupId,
        name,
        isVisibleIfGroupIsActive:
          isFirstPresentation || shouldOpenInSeparateTabs,
        isVisibleIfGroupIsInactive: isFirstPresentation,
        showNonRelatedGroupTabsIfActive: false,
        focusOnCreate: isFirstPresentation,
        closeGroupOnClose: true,
        mergingOptions: {
          allowed: true,
          key: Number(presentation.presentationId),
          index,
          isDetached: isFirstPresentation ? false : shouldOpenInSeparateTabs,
          detachedTitle: presentation.name,
        },
      },
      analyticsSessionId,
      forClient: playSubset.forClient,
      presentationId: String(presentation.presentationId),
      presentationType: 'external_url_presentation',
      playId: playSubset.id,
      createGlobalTabOnElectron: {
        startOn: 'tab-creation',
        withInterceptor: false,
        withWebSecurity: true,
      },
    });
  };

  const openPlaybook = async ({
    playSubset,
    data,
    isPreview,
    shouldClosePreviewTab = false,
  }: PlaybookPresentationsProps) => {
    const playId = playSubset.id;
    const { isDeleted } = playSubset;

    const groupId = generateGroupId(
      Number(playId),
      isPreview ? TabGroupName.PlaybookPreview : undefined
    );
    let playData = data;
    if (!playData) {
      playData = await getPlayData({
        organizationId,
        playId,
      }).unwrap();
    }

    const presentations = playSubset.presentations.map(({ presentationId }) => {
      return playData?.presentations.find(
        (presentationData) =>
          presentationData.presentationId === Number(presentationId)
      )?.playData as
        | PresentationByTokenResponse
        | TourPresentationByTokenResponse
        | ExternalUrlPresentationByTokenResponse;
    });

    const playbook =
      multiTabDisplay && playData?.asset.assetId
        ? await getPlaybook({
            workspaceId: playSubset.workspaceId,
            organizationId,
            id: playData.asset.assetId,
          }).unwrap()
        : undefined;

    const shouldOpenInSeparateTabs = Boolean(
      multiTabDisplay && playbook?.playSettings?.multiTab
    );

    const appSettings = await window?.dsDesktop?.appSettings?.getAppSettings();
    const appSettingsIsMouseHighlightActive =
      appSettings?.[SettingMainItemsKeys.MouseAnimation][
        SettingsSubKeys.MouseAnimationToggleState
      ];
    const appSettingsIsOpenByDefault =
      appSettings?.[SettingMainItemsKeys.PresentationDisplay][
        SettingsSubKeys.ShowSpeakerPanelByDefault
      ];

    // create speaker panel data
    const initialSpeakerPanelItems = presentations.map(
      (presentation, index) => {
        const relatedPresentation = presentations[index];
        const isFirstPresentation = index === 0;

        return {
          presentationName: presentation.name,
          image:
            'image' in presentation && presentation.image
              ? presentation.image
              : '',
          notes: relatedPresentation.presentationNotes ?? '',
          id: Number(getMergingOptionKey(relatedPresentation)),
          screenshotImageType: getScreenshotImageType(presentation),
          canDetach: isFirstPresentation ? true : !shouldOpenInSeparateTabs,
        };
      }
    );

    const initialSpeakerPanelData: SpeakerPanelData = {
      currentTime: 0,
      isNotesOpen: false,
      isWindowOpen: false,
      isOpen: appSettingsIsOpenByDefault ?? false,
      activePresentationIndex: 0,
      demosInfo: initialSpeakerPanelItems,
      isMouseHighlightActive: appSettingsIsMouseHighlightActive ?? true,
    };

    setIsSpeakerPanelWarningVisible(!!isPreview && !isDeleted);

    const name = isPreview
      ? TabGroupName.PlaybookPreview
      : TabGroupName.PlaybookPresentation;

    await window?.dsDesktop?.speakerPanel.setData(
      groupId,
      initialSpeakerPanelData
    );

    const { assetId } = playData.asset;

    if (!isPreview && shouldClosePreviewTab) {
      closePlayPreviewTabs(assetId);
    }

    const analyticsSessionId = !isPreview
      ? window.dsDesktop.analyticsClient.start({
          playId: playSubset.id,
          presentationId: playSubset.id,
          presentationType: 'play',
          resourceType: 'play',
          forClient: playSubset.forClient,
        })
      : '';

    presentations.forEach((presentation, index) => {
      playPresentationPerType(presentation, {
        onPlayDemo: (presentationData) =>
          handlePlayDemo({
            workspaceId: playSubset.workspaceId,
            playSubset,
            groupId,
            type: isPreview
              ? TabType.GROUP_DEMO_PREVIEW
              : TabType.GROUP_DEMO_PRESENTATION,
            name,
            presentation: presentationData,
            index,
            shouldOpenInSeparateTabs,
            analyticsSessionId,
          }),
        onPlayTour: (presentationData) =>
          handlePlayTour({
            workspaceId: playSubset.workspaceId,
            playSubset,
            groupId,
            type: isPreview
              ? TabType.GROUP_TOUR_PREVIEW
              : TabType.GROUP_TOUR_PRESENTATION,
            name,
            presentation: presentationData,
            index,
            shouldOpenInSeparateTabs,
            analyticsSessionId,
          }),
        onPlayExternalUrl: (presentationData) =>
          handlePlayExternalUrl({
            workspaceId: playSubset.workspaceId,
            playSubset,
            groupId,
            name,
            type: TabType.BROWSER_WINDOW,
            presentation: presentationData,
            index,
            shouldOpenInSeparateTabs,
            analyticsSessionId,
          }),
      });
    });
  };

  const handlePlayPlaybook = (data: PlaybookPresentationsProps) => {
    return openPlaybook(data);
  };

  const handlePreviewPlaybook = async (
    workspaceId: string,
    playbookId: string,
    play?: Play
  ) => {
    try {
      if (play) {
        await openPlaybook({
          playSubset: play,
          isPreview: true,
        });
        return;
      }

      const playbook = await getPlaybook({
        workspaceId,
        organizationId,
        id: playbookId,
      }).unwrap();
      const data = await getPlaybookPreview({
        workspaceId,
        organizationId,
        id: playbookId,
      }).unwrap();
      const playSubset = {
        workspaceId,
        presentations: data.presentations,
        id: String(data.presentationId),
        forClient: playbook.name,
        playbookId,
        ...(playbook.isDeleted && {
          isDeleted: playbook.isDeleted,
        }),
      };
      await openPlaybook({
        playSubset,
        data,
        isPreview: true,
      });
    } catch (error) {
      console.error('Error while previewing playbook', error);
    }
  };

  return {
    handlePlayPlaybook,
    handlePreviewPlaybook,
    closePlayPreviewTabs,
  };
};
